import React, { useMemo, useState, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import App from "./App.js";
import { BrowserRouter, useLocation } from "react-router-dom";
import { createAppTheme } from "./theme";
import { Analytics } from "@vercel/analytics/react";
import {
  ThemeModeContext,
  THEME_STORAGE_KEY_APP,
  THEME_STORAGE_KEY_PUBLIC,
} from "./themeMode";

function readAppMode() {
  try {
    const s = localStorage.getItem(THEME_STORAGE_KEY_APP);
    if (s === "dark" || s === "light") return s;
  } catch (_) {}
  return "light";
}

function readPublicMode() {
  try {
    const s = localStorage.getItem(THEME_STORAGE_KEY_PUBLIC);
    if (s === "dark" || s === "light") return s;
  } catch (_) {}
  return "light";
}

/** Marketing home and customer quote pages — separate appearance from dashboard. */
function isPublicAppearancePath(pathname) {
  if (pathname === "/") return true;
  if (pathname === "/quote") return true;
  if (pathname.startsWith("/quote/")) return true;
  return false;
}

function RootTheme({ children }) {
  const { pathname } = useLocation();
  const publicPath = isPublicAppearancePath(pathname);
  const [appMode, setAppMode] = useState(readAppMode);
  const [publicMode, setPublicMode] = useState(readPublicMode);

  const mode = publicPath ? publicMode : appMode;

  const setColorMode = useCallback(
    (next) => {
      if (publicPath) setPublicMode(next);
      else setAppMode(next);
    },
    [publicPath]
  );

  useLayoutEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY_APP, appMode);
    } catch (_) {}
  }, [appMode]);

  useLayoutEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY_PUBLIC, publicMode);
    } catch (_) {}
  }, [publicMode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useLayoutEffect(() => {
    document.body.classList.toggle("theme-dark", mode === "dark");
  }, [mode]);

  const modeApi = useMemo(() => ({ mode, setColorMode }), [mode, setColorMode]);

  return (
    <ThemeModeContext.Provider value={modeApi}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

function Root() {
  return (
    <BrowserRouter>
      <RootTheme>
        <App />
      </RootTheme>
    </BrowserRouter>
  );
}

ReactDOM.render(
  <React.StrictMode>
    <Analytics />
    <Root />
  </React.StrictMode>,
  document.getElementById("root")
);

reportWebVitals();
